import React, { LegacyRef, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMutationObservable } from '../../common/hooks/use-mutation-observable';
import movePreview from '../../assets/images/movePreview.png';
import { Box, cssVar } from '@cad-arc';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { ColumnDefinitionEnumType } from '@mark43/cad-api';

const mutationObserverOptions = {
    config: {
        attributes: true,
        attributeFilter: ['style'],
    },
};

export function MovePreview(
    props: React.HTMLAttributes<HTMLDivElement> & { position?: 'left' | 'right' }
) {
    return (
        <div
            {...props}
            style={{
                backgroundImage: `url(${movePreview})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                width: 14,
                height: 44,
                margin: 'auto',
                position: 'absolute',
                top: -12,
                zIndex: 1,
                display: props.position === undefined ? 'none' : 'block',
                ...(props.position === 'left' ? { left: -7 } : {}),
                ...(props.position === 'right' ? { right: -7 } : {}),
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

export const ColumnHeader = React.forwardRef(function ColumnHeader(
    props: React.HTMLAttributes<HTMLDivElement> & { canDrop?: boolean },
    ref: LegacyRef<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            {...props}
            style={{
                padding: '3px 7px',
                resize: 'horizontal',
                minWidth: '38px',
                overflow: 'hidden',
                cursor: 'move',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                fontSize: '12px',
                color: cssVar('arc.colors.text.secondary'),
                ...(props.canDrop ? { mixBlendMode: 'luminosity', backgroundColor: cssVar('arc.colors.text.secondary'), color: cssVar('arc.colors.surface.foreground') } : {}),
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
});

export function ResizeIndicator(props: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            {...props}
            style={{
                width: '10px',
                height: '10px',
                position: 'absolute',
                bottom: 0,
                right: 0,
                border: '5px solid var(--arc-colors-selected-default)',
                borderLeftColor: 'transparent',
                borderTopColor: 'transparent',
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

type DropPosition = 'left' | 'right' | undefined;

/**
 * Resizable and draggable queue column header
 */
export const QueueColumnHeader = observer(function QueueColumnHeader({
    columnDefinition,
    header,
    onResize,
    onSort,
    index,
    onReorder,
    styles,
}: {
    columnDefinition: ColumnDefinitionEnumType;
    header: React.ReactNode;
    index: number;
    onResize: (width: string, columnKey: ColumnDefinitionEnumType) => void;
    onReorder: (columnKey: ColumnDefinitionEnumType, newPosition: number) => void;
    onSort?: (columnKey: ColumnDefinitionEnumType) => void;
    styles?: React.CSSProperties;
}): React.ReactElement {
    const panel = useWorkspacePanel();
    const itemRef = useRef<HTMLDivElement>(null);
    const isCurrentlyResizingRef = useRef(false);
    const isUserHoveredOverElementRef = useRef(false);
    const [showResizeIndicator, setShowResizeIndicator] = useState(false);
    const [dropPosition, setDropPosition] = useState<DropPosition>(undefined);
    const mutationCallback = useCallback(
        (records: MutationRecord[]) => {
            const [record] = records;
            const width = (record.target as HTMLElement).style.width;
            if (width) {
                onResize(width, columnDefinition);
            }
        },
        [onResize, columnDefinition]
    );
    useMutationObservable(itemRef.current, mutationCallback, mutationObserverOptions);

    useEffect(
        function hideResizeIndicatorOnMouseup() {
            if (!itemRef.current) {
                return;
            }
            const el = itemRef.current;
            const resizeObserver = new ResizeObserver(() => {
                if (
                    isCurrentlyResizingRef.current !== true &&
                    isUserHoveredOverElementRef.current
                ) {
                    document.addEventListener(
                        'mouseup',
                        (event) => {
                            isCurrentlyResizingRef.current = false;
                            if (!itemRef.current) {
                                return;
                            }
                            if (
                                event.target !== itemRef.current ||
                                (event.target instanceof Node &&
                                    !itemRef.current.contains(event.target))
                            ) {
                                setShowResizeIndicator(false);
                                isUserHoveredOverElementRef.current = false;
                            }
                        },
                        { once: true }
                    );
                    isCurrentlyResizingRef.current = true;
                }
            });

            resizeObserver.observe(el);

            return () => {
                resizeObserver.unobserve(el);
            };
        },
        [itemRef]
    );

    return (
        <Box position="relative" display="inline-block" backgroundColor="surface.accent">
            <ColumnHeader
                canDrop={!!dropPosition}
                ref={itemRef}
                data-testid={`${columnDefinition}-header`}
                style={{ resize: showResizeIndicator ? 'horizontal' : 'none', ...(styles ?? {}) }}
                onClick={(e) => {
                    // always sort if we click from a test
                    if (process.env.NODE_ENV !== 'test') {
                        const rect = e.currentTarget.getBoundingClientRect();
                        // this ensures that resizing isn't triggering a sort
                        if (e.clientX > rect.x + rect.width - 18) {
                            return;
                        }
                    }
                    onSort?.(columnDefinition);
                }}
                onMouseOver={() => {
                    isUserHoveredOverElementRef.current = true;
                    setShowResizeIndicator(true);
                }}
                onMouseOut={() => {
                    if (!isCurrentlyResizingRef.current) {
                        setShowResizeIndicator(false);
                    }
                    isUserHoveredOverElementRef.current = false;
                }}
                draggable={true}
                onDrop={(e) => {
                    e.stopPropagation();
                    const data = e.dataTransfer.getData('text/plain');
                    if (data) {
                        const [columnDefinition, panelId] = data.split('|');
                        if (panelId === panel.initialId.toString()) {
                            const { x, width } = e.currentTarget.getBoundingClientRect();
                            const dropPosition = e.clientX;
                            onReorder(
                                columnDefinition as ColumnDefinitionEnumType,
                                dropPosition > x + width / 2 ? index + 1 : index
                            );
                        }
                        setDropPosition(undefined);
                    }
                }}
                onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.setData('text/plain', `${columnDefinition}|${panel.id}`);
                }}
                onDragLeave={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setDropPosition(undefined);
                }}
                onDragOver={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const { x, width } = e.currentTarget.getBoundingClientRect();
                    const dropPosition = e.clientX;
                    setDropPosition(dropPosition > x + width / 2 ? 'right' : 'left');
                }}
            >
                <div style={{ pointerEvents: 'none' }}>
                    <MovePreview position={dropPosition} />
                    {header}
                    {showResizeIndicator && <ResizeIndicator />}
                </div>
            </ColumnHeader>
        </Box>
    );
});
